import React, { useState } from 'react';
import axios from 'axios';
import { ShieldCheck, Trash2, Mail, Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import { useBranding } from '@/lib/branding';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DATA_DELETED = [
  'Your account profile (name, email, phone, role and login credentials)',
  'Shift, timesheet and attendance records you created',
  'Progress notes, incident reports, handover and transport logs you authored',
  'Photos, documents and attachments you uploaded',
  'Device tokens and notification preferences',
];

const DATA_RETAINED = [
  'Records we are legally required to keep for NDIS compliance, safeguarding, incident and financial audit obligations (retained for the minimum statutory period, then deleted).',
];

export default function DeleteAccountPage() {
  const { organisationName } = useBranding();
  const [form, setForm] = useState({ email: '', full_name: '', reason: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setMessage('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/public/account-deletion/request`, form);
      if (res.data?.ok) {
        setStatus('success');
        setMessage(res.data.message);
      } else {
        setStatus('error');
        setMessage(res.data?.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.message || 'Unable to submit your request right now. Please email us directly.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800" data-testid="delete-account-page">
      <div className="max-w-3xl mx-auto px-5 py-12 sm:py-16">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-12 w-12 rounded-xl bg-slate-900 text-white flex items-center justify-center">
            <Trash2 className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold tracking-widest text-slate-500 uppercase">{organisationName}</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Delete your account</h1>
          </div>
        </div>

        <p className="text-base leading-relaxed text-slate-600 mb-8">
          You can request permanent deletion of your {organisationName} account and associated
          personal data at any time. This page explains what is deleted, what may be
          retained for legal reasons, and how to submit your request.
        </p>

        <div className="grid gap-6 sm:grid-cols-2 mb-10">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2 mb-4 text-slate-900">
              <Trash2 className="h-5 w-5 text-red-600" />
              <h2 className="text-lg font-semibold">What gets deleted</h2>
            </div>
            <ul className="space-y-2 text-sm text-slate-600">
              {DATA_DELETED.map((item) => (
                <li key={item} className="flex gap-2">
                  <CheckCircle2 className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2 mb-4 text-slate-900">
              <ShieldCheck className="h-5 w-5 text-amber-600" />
              <h2 className="text-lg font-semibold">What may be retained</h2>
            </div>
            <ul className="space-y-2 text-sm text-slate-600">
              {DATA_RETAINED.map((item) => (
                <li key={item} className="flex gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex items-center gap-2 text-sm font-medium text-slate-700">
              <Clock className="h-4 w-4 text-slate-400" />
              Requests are actioned within 30 days.
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-1">Request account deletion</h2>
          <p className="text-sm text-slate-500 mb-6">
            Submit the email address associated with your account. We will verify your
            identity before deleting any data.
          </p>

          {status === 'success' ? (
            <div className="rounded-xl bg-green-50 border border-green-200 p-5 flex gap-3" data-testid="delete-account-success">
              <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />
              <div>
                <p className="font-semibold text-green-800">Request received</p>
                <p className="text-sm text-green-700 mt-1">{message}</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email address *</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@example.com"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                  data-testid="delete-account-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Full name (optional)</label>
                <input
                  type="text"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="Your full name"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                  data-testid="delete-account-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reason (optional)</label>
                <textarea
                  rows={3}
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  placeholder="Let us know why you're leaving (optional)"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                  data-testid="delete-account-reason"
                />
              </div>

              {status === 'error' && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 flex gap-2 text-sm text-red-700" data-testid="delete-account-error">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{message}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-semibold py-3 transition-colors"
                data-testid="delete-account-submit"
              >
                {status === 'loading' ? 'Submitting…' : 'Request account deletion'}
              </button>
            </form>
          )}

          <div className="mt-6 pt-6 border-t border-slate-100 flex items-start gap-2 text-sm text-slate-500">
            <Mail className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Prefer email? Send your deletion request to{' '}
              <a href="mailto:privacy@getcare360.com.au" className="font-medium text-slate-800 underline">
                privacy@getcare360.com.au
              </a>{' '}
              from your account email address.
            </span>
          </div>
        </div>

        <p className="text-xs text-slate-400 mt-8 text-center">
          {organisationName} · Account &amp; data deletion · This request applies to the {organisationName} mobile and web platform.
        </p>
      </div>
    </div>
  );
}
